/**
 * Centralized Error Handler
 * Handles error display and success messages using react-hot-toast
 */

import toast from 'react-hot-toast';
import { getErrorMessage, isAuthError } from './errorMessages';

/**
 * Handle and display error with user-friendly message
 * @param {Error|Object} error - Error object from API or network
 * @param {string|null} customMessage - Optional custom message to override default
 * @param {Object} options - Toast options
 * @returns {Object} - { message: string, code: string|null }
 */
export const handleError = (error, customMessage = null, options = {}) => {
  const { message, code } = getErrorMessage(error);

  const displayMessage = customMessage || message;
  const errorReference = code ? ` (Error: ${code})` : '';

  // Check if error is authentication related
  if (isAuthError(error)) {
    toast.error(displayMessage + errorReference, {
      duration: 6000,
      icon: '🔒',
      ...options
    });

    // Optionally redirect to login (uncomment if needed)
    // setTimeout(() => {
    //   window.location.href = '/login';
    // }, 2000);
  } else {
    toast.error(displayMessage + errorReference, {
      duration: 5000,
      icon: '❌',
      ...options
    });
  }

  return { message: displayMessage, code };
};

/**
 * Display success message
 * @param {string} message - Success message to display
 * @param {number} duration - Duration in milliseconds (default: 3000)
 * @param {Object} options - Additional toast options
 */
export const handleSuccess = (message, duration = 3000, options = {}) => {
  toast.success(message, {
    duration,
    icon: '✅',
    ...options
  });
};

/**
 * Display info message
 * @param {string} message - Info message to display
 * @param {number} duration - Duration in milliseconds (default: 3000)
 * @param {Object} options - Additional toast options
 */
export const handleInfo = (message, duration = 3000, options = {}) => {
  toast(message, {
    duration,
    icon: 'ℹ️',
    ...options
  });
};

/**
 * Display warning message
 * @param {string} message - Warning message to display
 * @param {number} duration - Duration in milliseconds (default: 4000)
 * @param {Object} options - Additional toast options
 */
export const handleWarning = (message, duration = 4000, options = {}) => {
  toast(message, {
    duration,
    icon: '⚠️',
    style: {
      background: '#f59e0b',
      color: '#fff'
    },
    ...options
  });
};

/**
 * Display loading toast
 * @param {string} message - Loading message to display
 * @param {Object} options - Additional toast options
 * @returns {string} - Toast ID (use to dismiss later)
 */
export const handleLoading = (message, options = {}) => {
  return toast.loading(message, options);
};

/**
 * Dismiss a specific toast or all toasts
 * @param {string|null} toastId - Toast ID to dismiss (null to dismiss all)
 */
export const dismissToast = (toastId = null) => {
  if (toastId) {
    toast.dismiss(toastId);
  } else {
    toast.dismiss();
  }
};

/**
 * Promise-based toast handler for async operations
 * @param {Promise} promise - Promise to handle
 * @param {Object} messages - { loading, success, error }
 * @param {Object} options - Toast options
 * @returns {Promise} - Original promise result
 */
export const handlePromise = (promise, messages, options = {}) => {
  return toast.promise(
    promise,
    {
      loading: messages.loading || 'Processing...',
      success: messages.success || 'Success!',
      error: (err) => {
        const { message } = getErrorMessage(err);
        return messages.error || message;
      }
    },
    options
  );
};

/**
 * Handle API call with automatic error/success handling
 * @param {Function} apiCall - Async function that makes API call
 * @param {Object} messages - { success, error }
 * @param {Object} options - Options for error/success handling
 * @returns {Promise} - Result of API call or null if error
 */
export const handleApiCall = async (apiCall, messages = {}, options = {}) => {
  try {
    const result = await apiCall();

    if (messages.success) {
      handleSuccess(messages.success, options.successDuration);
    }

    return result;
  } catch (error) {
    handleError(error, messages.error, options);

    if (options.rethrow) {
      throw error;
    }

    return null;
  }
};

/**
 * Create a debounced error handler to prevent duplicate errors
 * @param {number} delay - Delay in milliseconds (default: 1000)
 * @returns {Function} - Debounced error handler
 */
export const createDebouncedErrorHandler = (delay = 1000) => {
  let timeoutId = null;
  let lastError = null;

  return (error, customMessage = null, options = {}) => {
    const errorString = JSON.stringify({ error: error.message, customMessage });

    // Clear existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Only show error if it's different from last error
    if (errorString !== lastError) {
      handleError(error, customMessage, options);
      lastError = errorString;
    }

    // Reset after delay
    timeoutId = setTimeout(() => {
      lastError = null;
      timeoutId = null;
    }, delay);
  };
};

/**
 * Batch error handler - collects multiple errors and displays summary
 */
export class BatchErrorHandler {
  constructor() {
    this.errors = [];
  }

  add(error, context = '') {
    const { message, code } = getErrorMessage(error);
    this.errors.push({ message, code, context });
  }

  hasErrors() {
    return this.errors.length > 0;
  }

  getCount() {
    return this.errors.length;
  }

  display(customMessage = null) {
    if (this.errors.length === 0) return;

    if (this.errors.length === 1) {
      handleError({ message: this.errors[0].message }, customMessage);
    } else {
      const message = customMessage || `${this.errors.length} errors occurred`;
      toast.error(message, {
        duration: 6000,
        icon: '❌'
      });

      // Log all errors in development
      if (import.meta.env.MODE === 'development') {
        console.error('[Batch Errors]', this.errors);
      }
    }

    this.clear();
  }

  clear() {
    this.errors = [];
  }
}
